// The feature runtime of the app (ADR-0027 D9).
//
// `AppFeatureProvider` joins the two halves of a feature check: the build
// gives the enabled features (`virtual:septima-features`), and the session
// gives the release holds of the deployment (ADR-0027 D12), the AI seats of
// the user and the active persona. A held feature is in the build, but it is
// off: its routes show NotFound, and its sidebar rows and slots do not show. Mount it inside
// `SessionProvider`, so each page can call `useFeature`, `useSlot` and
// `useSlotData` of `@septima/feature-core/react`.
//
// The session also gives the hash of the Feature Manifest of the API. When
// the hash differs from the hash of this build, the API runs a different
// deployment (a deploy skew), and the shell asks the user once to reload.
import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import type { SeatKey } from '@septima/feature-core';
import { FeatureProvider } from '@septima/feature-core/react';
import { useSession } from '@/contexts/SessionContext';
import { useSessionQuery } from '@/lib/auth';
import { useToast } from '@/lib/useToast';
import {
    APP_FEATURES,
    ENABLED,
    GRANTS_ALL_SEATS,
    MANIFEST_HASH,
    SEAT_OF,
} from '@/lib/feature-registry';

const NO_SEATS: readonly SeatKey[] = [];

/** The enabled ids of the build, less each held id and its subtree. */
export function withoutHeld(
    enabled: ReadonlySet<string>,
    held: readonly string[],
): ReadonlySet<string> {
    if (held.length === 0) return enabled;
    return new Set(
        [...enabled].filter((id) => !held.some((h) => id === h || id.startsWith(`${h}.`))),
    );
}

/** The text of the notice for a deploy skew. */
export const MANIFEST_SKEW_MESSAGE = 'A new version of Septima is available. Reload the page.';

/** True when the session names a manifest hash and it is not the hash of this build. */
export function isManifestSkew(sessionHash: string | undefined, buildHash: string): boolean {
    return sessionHash !== undefined && sessionHash !== '' && sessionHash !== buildHash;
}

/** Shows the reload notice once for each hash of the API that differs from the build. */
function ManifestSkewNotice({ sessionHash }: { sessionHash: string | undefined }) {
    const { pushToast } = useToast();
    const shown = useRef<string | null>(null);
    useEffect(() => {
        if (!isManifestSkew(sessionHash, MANIFEST_HASH) || shown.current === sessionHash) return;
        shown.current = sessionHash ?? null;
        pushToast({ kind: 'info', message: MANIFEST_SKEW_MESSAGE });
    }, [sessionHash, pushToast]);
    return null;
}

export function AppFeatureProvider({ children }: { children: ReactNode }) {
    const { user } = useSession();
    const { data } = useSessionQuery();
    const sessionFeatures = data?.features;
    const seatKey = sessionFeatures?.seats.join(',') ?? '';
    // A stable array for each set of seats, so the provider value changes
    // only when the seats change.
    const seats = useMemo<readonly SeatKey[]>(
        () => (seatKey === '' ? NO_SEATS : (seatKey.split(',') as SeatKey[])),
        [seatKey],
    );
    const heldKey = sessionFeatures?.held.join(',') ?? '';
    const enabled = useMemo(
        () => withoutHeld(ENABLED, heldKey === '' ? [] : heldKey.split(',')),
        [heldKey],
    );
    return (
        <FeatureProvider
            features={APP_FEATURES}
            enabled={enabled}
            seatOf={SEAT_OF}
            grantsAllSeats={GRANTS_ALL_SEATS || (sessionFeatures?.grantsAllSeats ?? false)}
            seats={seats}
            persona={user?.activePersona ?? null}
        >
            <ManifestSkewNotice sessionHash={sessionFeatures?.manifestHash} />
            {children}
        </FeatureProvider>
    );
}
